import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import clsx from 'clsx'
import { store } from '@/src/stores/store'
import { observer } from 'mobx-react-lite'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { playCharacterStretchSound } from '@/src/game/audio/characterEventSound'
import { playCountdownSound } from '@/src/game/audio/uiSounds'



type Phase = 'counting' | 'phaseIntro' | 'phaseOutro'

const GO_SCREEN_MS = 1200
const PHASE_POPUP_SHOW_MS = 0
const PHASE_POPUP_EXIT_MS = 0

type CountdownScreenProps = {
    onDone: () => void
}

const CountdownScreen = observer(function CountdownScreen({
    onDone,
}: CountdownScreenProps) {
    const [phase, setPhase] = useState<Phase>('counting')
    const [count, setCount] = useState(4)
    const hasPlayedStretchRef = useRef(false)
    const settingsOpen = store.settingsOpen
    const isStele = useSteleBreakpoint();

    useEffect(() => {
        if (phase !== 'counting' || settingsOpen || count !== 4 || hasPlayedStretchRef.current) return
        hasPlayedStretchRef.current = true
        playCharacterStretchSound()
    }, [count, phase, settingsOpen])

    useEffect(() => {
        if (phase !== 'counting' || settingsOpen || count !== 3) return
        playCountdownSound()
    }, [count, phase, settingsOpen])

    useEffect(() => {
        if (phase !== 'counting' || settingsOpen) return
        if (count === 0) {
            const t = window.setTimeout(() => setPhase('phaseIntro'), GO_SCREEN_MS)
            return () => window.clearTimeout(t)
        }
        const t = window.setTimeout(() => setCount(c => c - 1), count === 4 ? 2000 : 1000)
        return () => window.clearTimeout(t)
    }, [count, phase, settingsOpen])

    useEffect(() => {
        if (phase !== 'phaseIntro') return
        const t = window.setTimeout(() => setPhase('phaseOutro'), PHASE_POPUP_SHOW_MS)
        return () => window.clearTimeout(t)
    }, [phase])

    useEffect(() => {
        if (phase !== 'phaseOutro') return
        const t = window.setTimeout(onDone, PHASE_POPUP_EXIT_MS)
        return () => window.clearTimeout(t)
    }, [phase, onDone])

    const getCountdownSrc = () => {
        switch (count) {
            case 4: return '/images/countdown/Ready.png'
            case 3: return '/images/countdown/Countdown3.png'
            case 2: return '/images/countdown/Countdown2.png'
            case 1: return '/images/countdown/Countdown1.png'
            default: return '/images/countdown/Lauf.png'
        }
    }

    return (
        <div className='absolute inset-0 bg-black/35'>
            <div className='absolute inset-0 flex justify-center items-center'>
                <AnimatePresence mode='wait'>
                    {phase === 'counting' && (
                        <div
                            className={clsx(
                                count === 0 && 'stele:w-463.5 stele:h-182.5 stele:bottom-280 w-77.25 h-30.5 stele:absolute',
                                count === 4 && 'stele:w-292 stele:h-116 w-73 h-29 stele:absolute stele:bottom-325',
                                (count !== 0 && count !== 4) && 'stele:w-384 stele:h-384 stele:bottom-200 w-57.5 h-57.5 stele:absolute'
                            )}
                        >
                            <motion.img
                                key={count}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.85 }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 520,
                                    damping: 30,
                                    mass: 0.7,
                                }}
                                src={getCountdownSrc()}
                                className='w-full h-full object-contain'
                                alt='countdown'
                            />
                        </div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
})

export default CountdownScreen
